import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from "recharts";
import {
  TrendingUp, TrendingDown, Activity, AlertTriangle, Bell,
  Loader2, RefreshCw, Shield, Package, Trash2, Trophy,
  Settings, CheckCircle2, Send, Clock, Minus, FileDown, FileText
} from "lucide-react";

const fmt = (v) =>
  new Intl.NumberFormat("fr-CI", { style: "currency", currency: "XOF", maximumFractionDigits: 0 }).format(v);

const VariationBadge = ({ value }) => {
  if (value === 0) return <Badge variant="outline" className="text-slate-400 border-slate-600 gap-1"><Minus className="w-3 h-3" /> 0%</Badge>;
  const up = value > 0;
  return (
    <Badge variant="outline" className={`gap-1 ${up ? "text-red-400 border-red-500/30" : "text-emerald-400 border-emerald-500/30"}`}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {up ? "+" : ""}{value}%
    </Badge>
  );
};

const SEVERITY_STYLE = {
  critical: { bg: "bg-red-500/100/15", border: "border-red-500/30", text: "text-red-400", icon: AlertTriangle },
  warning: { bg: "bg-amber-500/100/15", border: "border-amber-500/30", text: "text-amber-400", icon: Bell },
  info: { bg: "bg-sky-500/15", border: "border-sky-500/30", text: "text-sky-400", icon: Activity },
};

const MonthlyDashboardPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [alertConfig, setAlertConfig] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, configRes, histRes] = await Promise.all([
        axios.get(`${API}/monthly/dashboard`, { headers }),
        axios.get(`${API}/alerts/config`, { headers }),
        axios.get(`${API}/alerts/history?limit=20`, { headers }),
      ]);
      setData(dashRes.data);
      setAlertConfig(configRes.data);
      setAlerts(histRes.data);
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const checkAlerts = async () => {
    setChecking(true);
    try {
      await axios.post(`${API}/alerts/check`, {}, { headers });
      toast.success("Vérification lancée");
      setTimeout(async () => {
        const res = await axios.get(`${API}/alerts/history?limit=20`, { headers });
        setAlerts(res.data);
        setChecking(false);
      }, 2000);
    } catch { toast.error("Erreur"); setChecking(false); }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/alerts/config`, alertConfig, { headers });
      toast.success("Configuration sauvegardée");
    } catch { toast.error("Erreur de sauvegarde"); }
    finally { setSaving(false); }
  };

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/reports/monthly/pdf`, { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `rapport_mensuel_${new Date().toISOString().slice(0, 7)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Rapport mensuel téléchargé");
    } catch { toast.error("Erreur de téléchargement"); }
    finally { setDownloading(false); }
  };

  const sendAllReports = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/reports/monthly/send-all`, {}, { headers });
      toast.success(`Envoi en cours à ${res.data.pharmacies_count} pharmacies`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'envoi");
    }
    finally { setSending(false); }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
        </div>
      </Layout>
    );
  }

  const k = data?.kpis || {};
  const trend = data?.monthly_trend || [];
  const ranking = data?.ranking || {};

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-6" data-testid="monthly-dashboard-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-outfit font-bold text-white flex items-center gap-3">
              <Activity className="w-8 h-8 text-sky-400" />
              Tableau de Bord Mensuel
            </h1>
            <p className="text-slate-400 mt-1">{data?.current_month} — Comparaison & alertes automatiques</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={downloadReport} disabled={downloading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="download-report-btn">
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              Rapport PDF
            </Button>
            <Button onClick={sendAllReports} disabled={sending} variant="outline"
              className="border-amber-500/30 text-amber-400 hover:bg-amber-500/100/100/10" data-testid="send-all-reports-btn">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Envoyer à tous
            </Button>
            <Button onClick={() => setShowSettings(!showSettings)} variant="outline"
              className="border-white/10 text-slate-300 hover:bg-white/5" data-testid="toggle-settings-btn">
              <Settings className="w-4 h-4 mr-2" /> Seuils
            </Button>
            <Button onClick={checkAlerts} disabled={checking}
              className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="check-alerts-btn">
              {checking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
              Vérifier alertes
            </Button>
            <Button onClick={load} variant="outline" className="border-white/10 text-slate-300 hover:bg-white/5">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* ITPP + Ranking */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className={`glass-card col-span-1 border ${
            k.itpp_score >= 80 ? "border-emerald-500/30" : k.itpp_score >= 60 ? "border-sky-500/30" : k.itpp_score >= 40 ? "border-amber-500/30" : "border-red-500/30"
          }`} data-testid="itpp-overview-card">
            <CardContent className="p-5 text-center">
              <Shield className={`w-8 h-8 mx-auto mb-2 ${
                k.itpp_score >= 80 ? "text-emerald-400" : k.itpp_score >= 60 ? "text-sky-400" : k.itpp_score >= 40 ? "text-amber-400" : "text-red-400"
              }`} />
              <p className="text-4xl font-outfit font-bold text-white">{k.itpp_score}</p>
              <p className="text-slate-500 text-xs">/100</p>
              <Badge className={`mt-2 ${
                k.itpp_score >= 80 ? "bg-emerald-500/20 text-emerald-400" : k.itpp_score >= 60 ? "bg-sky-500/20 text-sky-400" : k.itpp_score >= 40 ? "bg-amber-500/100/20 text-amber-400" : "bg-red-500/100/20 text-red-400"
              }`}>{k.itpp_label}</Badge>
            </CardContent>
          </Card>

          <Card className="glass-card" data-testid="ranking-card">
            <CardContent className="p-5 text-center">
              <Trophy className="w-8 h-8 mx-auto mb-2 text-amber-400" />
              <p className="text-4xl font-outfit font-bold text-amber-400">{ranking.position}<span className="text-lg text-slate-500">/{ranking.total}</span></p>
              <p className="text-slate-500 text-xs">Classement</p>
              <Badge className="mt-2 bg-amber-500/100/20 text-amber-400">Top {ranking.percentile}%</Badge>
            </CardContent>
          </Card>

          <Card className="glass-card" data-testid="expired-card">
            <CardContent className="p-5 text-center">
              <AlertTriangle className={`w-8 h-8 mx-auto mb-2 ${k.expired_pct > 15 ? "text-red-400" : "text-slate-400"}`} />
              <p className="text-4xl font-outfit font-bold text-white">{k.expired_pct}%</p>
              <p className="text-slate-500 text-xs">Stock périmé</p>
              <p className="text-red-400 text-xs mt-1">{fmt(k.expired_value)}</p>
            </CardContent>
          </Card>

          <Card className="glass-card" data-testid="destructions-card">
            <CardContent className="p-5 text-center">
              <Trash2 className="w-8 h-8 mx-auto mb-2 text-sky-400" />
              <div className="flex items-center justify-center gap-2">
                <p className="text-4xl font-outfit font-bold text-white">{k.curr_destructions_count}</p>
                <VariationBadge value={k.destructions_variation} />
              </div>
              <p className="text-slate-500 text-xs">Destructions ce mois</p>
              <p className="text-sky-400 text-xs mt-1">{fmt(k.curr_destructions_value)}</p>
            </CardContent>
          </Card>
        </div>

        {/* KPI Détail Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Produits actifs", value: k.active_products, icon: Package, color: "text-emerald-400" },
            { label: "Produits périmés", value: k.expired_count, icon: AlertTriangle, color: k.expired_count > 0 ? "text-red-400" : "text-slate-400" },
            { label: "Expirent < 3 mois", value: k.expiring_3m_count, icon: Clock, color: k.expiring_3m_count > 0 ? "text-amber-400" : "text-slate-400" },
            { label: "Valeur stock", value: fmt(k.total_stock_value), icon: TrendingUp, color: "text-sky-400", raw: true },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <Card key={item.label} className="glass-card">
                <CardContent className="p-4 flex items-center gap-3">
                  <Icon className={`w-5 h-5 ${item.color} flex-shrink-0`} />
                  <div>
                    <p className="text-lg font-bold text-white">{item.raw ? item.value : item.value}</p>
                    <p className="text-slate-500 text-[11px]">{item.label}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base">Evolution des destructions (6 mois)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="month_label" tick={{ fill: "#94A3B8", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "#1E293B", border: "1px solid #334155", borderRadius: 8, color: "#E2E8F0" }} />
                  <Bar dataKey="destructions_count" name="Destructions" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="products_added" name="Produits ajoutés" fill="#10B981" radius={[4, 4, 0, 0]} />
                  <Legend wrapperStyle={{ color: "#94A3B8", fontSize: 11 }} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base">Valeur des destructions (6 mois)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="month_label" tick={{ fill: "#94A3B8", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ background: "#1E293B", border: "1px solid #334155", borderRadius: 8, color: "#E2E8F0" }}
                    formatter={(v) => [fmt(v), "Valeur"]} />
                  <Line type="monotone" dataKey="destructions_value" name="Valeur" stroke="#F59E0B" strokeWidth={2} dot={{ fill: "#F59E0B", r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Alert Settings */}
        {showSettings && alertConfig && (
          <Card className="glass-card border border-sky-500/30" data-testid="alert-settings-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-white flex items-center gap-2">
                <Settings className="w-5 h-5 text-sky-400" /> Configuration des alertes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Alertes activées</Label>
                <Switch checked={alertConfig.enabled}
                  onCheckedChange={(v) => setAlertConfig({ ...alertConfig, enabled: v })}
                  data-testid="alerts-enabled-switch" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm text-slate-400">Seuil ITPP critique (score min)</Label>
                  <Input type="number" value={alertConfig.itpp_critical}
                    onChange={(e) => setAlertConfig({ ...alertConfig, itpp_critical: parseInt(e.target.value) || 0 })}
                    className="bg-slate-900/50 border-white/10 text-white" data-testid="itpp-critical-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-slate-400">Seuil ITPP attention (score min)</Label>
                  <Input type="number" value={alertConfig.itpp_warning}
                    onChange={(e) => setAlertConfig({ ...alertConfig, itpp_warning: parseInt(e.target.value) || 0 })}
                    className="bg-slate-900/50 border-white/10 text-white" data-testid="itpp-warning-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-slate-400">% stock périmé critique</Label>
                  <Input type="number" value={alertConfig.expired_pct_critical}
                    onChange={(e) => setAlertConfig({ ...alertConfig, expired_pct_critical: parseInt(e.target.value) || 0 })}
                    className="bg-slate-900/50 border-white/10 text-white" data-testid="expired-critical-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-slate-400">% stock périmé attention</Label>
                  <Input type="number" value={alertConfig.expired_pct_warning}
                    onChange={(e) => setAlertConfig({ ...alertConfig, expired_pct_warning: parseInt(e.target.value) || 0 })}
                    className="bg-slate-900/50 border-white/10 text-white" data-testid="expired-warning-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-slate-400">Canaux de notification</Label>
                <div className="flex flex-wrap gap-3">
                  {[
                    { key: "in_app", label: "In-App" },
                    { key: "sms", label: "SMS" },
                    { key: "whatsapp", label: "WhatsApp" },
                  ].map((ch) => {
                    const active = alertConfig.channels?.includes(ch.key);
                    return (
                      <Button key={ch.key} variant={active ? "default" : "outline"} size="sm"
                        className={active ? "bg-sky-600 text-white" : "border-white/10 text-slate-400"}
                        data-testid={`channel-${ch.key}-btn`}
                        onClick={() => {
                          const channels = active
                            ? alertConfig.channels.filter((c) => c !== ch.key)
                            : [...(alertConfig.channels || []), ch.key];
                          setAlertConfig({ ...alertConfig, channels });
                        }}>
                        {ch.label}
                      </Button>
                    );
                  })}
                </div>
              </div>
              <Button onClick={saveConfig} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="save-alert-config-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Sauvegarder
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Alert History */}
        <Card className="glass-card" data-testid="alert-history-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-400" />
              Historique des alertes
              {alerts.length > 0 && (
                <Badge className="bg-amber-500/100/20 text-amber-400 ml-2">{alerts.length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {alerts.length > 0 ? (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {alerts.map((alert) => {
                  const style = SEVERITY_STYLE[alert.severity] || SEVERITY_STYLE.info;
                  const Icon = style.icon;
                  return (
                    <div key={alert.id} className={`flex items-start gap-3 p-3 rounded-lg ${style.bg} border ${style.border}`}
                      data-testid={`alert-${alert.id}`}>
                      <Icon className={`w-5 h-5 ${style.text} mt-0.5 flex-shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={`font-medium text-sm ${style.text}`}>{alert.title}</p>
                          <Badge variant="outline" className={`text-[10px] ${style.text} ${style.border}`}>
                            {alert.severity}
                          </Badge>
                        </div>
                        <p className="text-slate-400 text-xs mt-1">{alert.message}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-slate-600 text-[10px]">
                            {new Date(alert.sent_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                          </span>
                          <div className="flex gap-1">
                            {alert.channels_used?.map((ch) => (
                              <Badge key={ch} variant="outline" className="text-[9px] text-slate-500 border-slate-700 py-0">
                                {ch}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckCircle2 className="w-12 h-12 text-emerald-500/30 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">Aucune alerte envoyée</p>
                <p className="text-slate-600 text-xs mt-1">Vos indicateurs sont dans les seuils normaux</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default MonthlyDashboardPage;
